import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import BuyButtons from '../components/BuyButtons';

const groupByCategory = (products) => {
  return products.reduce((grouped, product) => {
    if (!grouped[product.category]) {
      grouped[product.category] = [];
    }
    grouped[product.category].push(product);
    return grouped;
  }, {});
};

const Home = forwardRef((_, ref) => {
  const [allProducts, setAllProducts] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useImperativeHandle(ref, () => ({
    resetCategory: () => setSelectedCategory(''),
  }));

  useEffect(() => {
    // Handle category from navigation state
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch('/.netlify/functions/get-products');
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || 'Failed to fetch products');
        }
        
        if (result.data && Array.isArray(result.data)) {
          setAllProducts(result.data);
          setProductsByCategory(groupByCategory(result.data));
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching products:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const productsToShow = selectedCategory && productsByCategory[selectedCategory]
    ? productsByCategory[selectedCategory]
    : allProducts;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center text-gray-500">
        Loading products...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!allProducts.length) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center text-gray-500">
        No products found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Helmet>
        <title>The Tech Nerd | Tech Product Reviews</title>
        <meta name="description" content="Expert tech product reviews and recommendations to help you make informed decisions. Find detailed reviews of the latest tech products." />
        <meta property="og:title" content="The Tech Nerd | Tech Product Reviews" />
        <meta property="og:description" content="Expert tech product reviews and recommendations to help you make informed decisions. Find detailed reviews of the latest tech products." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thetechnerd.co" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Tech Nerd | Tech Product Reviews" />
        <meta name="twitter:description" content="Expert tech product reviews and recommendations to help you make informed decisions. Find detailed reviews of the latest tech products." />
        <link rel="canonical" href="https://thetechnerd.co" />
      </Helmet>
      {/* Tagline Section */}
      <div className="w-full bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-lg sm:text-xl text-gray-700 font-medium">
            Easy to digest tech product reviews to help you make informed decisions!
          </p>
        </div>
      </div>

      {/* Category Selection */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            key="all"
            onClick={() => setSelectedCategory('')}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
              selectedCategory === '' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {Object.keys(productsByCategory).map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-6">
          {productsToShow.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col">
              <Link
                to={`/reviews/${encodeURIComponent(product.title)}`}
                className="relative w-full pt-[75%] sm:pt-[100%] bg-gray-100 block group"
                tabIndex={0}
                aria-label={`View review for ${product.title}`}
              >
                <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4 group-hover:scale-105 transition-transform">
                  <img
                    src={product.image}
                    alt={product.title}
                    loading="lazy"
                    decoding="async"
                    className="max-w-full max-h-full w-auto h-auto object-contain"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/300x300?text=${encodeURIComponent(product.title)}`;
                    }}
                  />
                </div>
              </Link>
              <div className="p-2 sm:p-4 flex-grow flex flex-col">
                <h3 className="text-sm sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2" dangerouslySetInnerHTML={{ __html: product.title }} />
                {product.description && (
                  <p className="text-xs text-gray-500 mb-2 sm:mb-4">{product.description}</p>
                )}
                <div className="mt-auto space-y-2">
                  <div className="flex flex-col space-y-2">
                    <Link
                      to={`/reviews/${encodeURIComponent(product.title)}`}
                      className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      Learn More
                    </Link>
                    <BuyButtons link={product.link} links={product.links} layout="stack" size="md" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

Home.displayName = "Home";

export default Home; 